import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocale } from '@gov-nx/core/service';
import type {
	Category,
	CrzResponse,
	ServiceCode,
} from '@gov-nx/module/service';
import { transformCrzDataToRecordsCompleteGunList } from '@gov-nx/module/service';
import { toDateReadable } from '@gov-nx/utils/common';
import { CategorySelectionService } from './service';
import { getUserDataFromCrzData } from './transformCrzDataToRecordsCompleteGunList';

/**
 * React hook for managing category selection with tree structure
 * Maintains internal state and provides callbacks for UI interactions
 *
 * @param categories - Available categories to track (required for initialization)
 * @param initialSelected - Initial array of selected category IDs
 * @param allSelected - Whether all categories should be selected by default
 * @returns Object with selection state and control methods
 */
export function useCategorySelection(
	categories: Record<string, Category | undefined> | undefined,
	initialSelected: string[] = [],
	allSelected: boolean = true
) {
	const serviceRef = useRef<CategorySelectionService | null>(null);
	/**
	 * This state exists solely to trigger React re-renders when selection changes.
	 * The actual selection state is managed internally by CategorySelectionService.
	 * Without this, React wouldn't know when to re-render components after toggle operations,
	 * causing stale checkbox states in the UI.
	 */
	const [, forceUpdate] = useState({});

	// Initialize service when categories become available
	useEffect(() => {
		if (categories && !serviceRef.current) {
			serviceRef.current = new CategorySelectionService(
				categories,
				initialSelected,
				allSelected
			);
			forceUpdate({});
		}
	}, [categories, initialSelected, allSelected]);

	/**
	 * Toggles entire category selection state
	 */
	const toggleCategory = useCallback(
		(categoryKey: string, category: Category) => {
			if (!serviceRef.current) return;
			serviceRef.current.toggleCategory(categoryKey, category);
			forceUpdate({});
		},
		[]
	);

	/**
	 * Toggles individual subcategory within a category
	 */
	const toggleSubcategory = useCallback(
		(categoryKey: string, category: Category, subcategoryId: string) => {
			if (!serviceRef.current) return;
			serviceRef.current.toggleSubcategory(
				categoryKey,
				category,
				subcategoryId
			);
			forceUpdate({});
		},
		[]
	);

	/**
	 * Toggles between selecting all categories and deselecting all
	 */
	const toggleAll = useCallback(() => {
		if (!serviceRef.current) return;
		serviceRef.current.toggleAll();
		forceUpdate({});
	}, []);

	/**
	 * Checks if a specific category or subcategory is selected
	 */
	const isSelected = useCallback((id: string): boolean => {
		if (!serviceRef.current) return false;
		return serviceRef.current.isSelected(id);
	}, []);

	/**
	 * Checks if a category is in an indeterminate state
	 * Returns true if some (but not all) of its items are selected
	 */
	const isIndeterminate = useCallback((categoryKey: string): boolean => {
		if (!serviceRef.current) return false;
		return serviceRef.current.isIndeterminate(categoryKey);
	}, []);

	/**
	 * Returns selected categories in expanded format
	 * Resolves wildcards to individual items when allSelected is true
	 * Note: Not memoized to always return current state from service
	 */
	const getSelectedExpanded = (): string[] => {
		if (!serviceRef.current) return [];
		return serviceRef.current.getSelectedExpanded();
	};

	return {
		selected: serviceRef.current?.getSelected() ?? [],
		allSelected: serviceRef.current?.isAllSelected() ?? false,
		toggleCategory,
		toggleSubcategory,
		isSelected,
		isIndeterminate,
		getSelectedExpanded,
		toggleAll,
	};
}

/**
 * Hook that provides person information from ROB
 * Returns data formatted as key-value pairs for display in tables
 *
 * @param code - Service code for translations
 * @returns Array of key-value pairs with person information
 */
export const usePersonInfo = (code: ServiceCode) => {
	const { t } = useLocale(code);
	const empty = '-';

	const getPersonRecordsFromCrzResponse = (response: CrzResponse) => {
		const userDataFromCrz = getUserDataFromCrzData(response);

		if (!userDataFromCrz) return;

		return [
			{
				key: t('row.label.prijmeni'),
				value: userDataFromCrz.prijmeni ?? empty,
			},
			{
				key: t('row.label.jmeno'),
				value: userDataFromCrz.jmeno ?? empty,
			},
			{
				key: t('row.label.datum-narozeni'),
				value: userDataFromCrz.datumNarozeni
					? toDateReadable(userDataFromCrz.datumNarozeni)
					: empty,
			},
			{
				key: t('row.label.typ-cislo-dokladu'),
				value: userDataFromCrz.dokladDrziteleType ?? empty,
			},
		];
	};

	return {
		getPersonRecordsFromCrzResponse,
	};
};

export const useHasCrzResults = (crzResponse: CrzResponse | undefined) =>
	useMemo(() => {
		if (!crzResponse) return false;

		const records = transformCrzDataToRecordsCompleteGunList(crzResponse);

		const hasOpravneni = Object.entries(records?.opravneni ?? {}).some(
			([_, val]) => !!val
		);

		const hasZbraneVyjimkyPovoleni = Object.entries(
			records?.zbraneVyjimkyPovoleni ?? {}
		).some(([_, val]) => !!val);

		return hasOpravneni || hasZbraneVyjimkyPovoleni;
	}, [crzResponse]);
